import React, {useEffect, useState} from 'react';

import {BackendSourceOptions, discoveryTracker} from '@udemy/browse-event-tracking';

import {TopicCard} from '../topic-card/topic-card.react-component';
import {EnrichedSkills} from './enriched-skills.react-component';
import {useLocaleContentfulData} from './hooks/useLocaleContentfulData';

interface TopicCard {
    title: string;
    url: string;
    learners: number;
    image: {
        url: string;
    };
}

export const EnrichedSkillsContainer = () => {
    const [locale, setLocale] = useState('en_US');
    const [isClient, setIsClient] = useState(false);
    const contentfulData = useLocaleContentfulData(locale);

    // Safely get locale from URL on client-side only
    useEffect(() => {
        setIsClient(true);
        if (typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search);
            const urlLocale = searchParams.get('locale') || 'en_US';
            setLocale(urlLocale);
        }
    }, []);

    const onClick = (topicCard: TopicCard, index: number) => {
        discoveryTracker.trackDiscoveryItemClickEvent(
            {
                item: {
                    id: index,
                    title: topicCard.title,
                    type: 'course_topic',
                },
            },
            {
                backendSource: 'discovery' as BackendSourceOptions,
                index: index,
                uiRegion: 'learn_essential_skills',
            },
        );
    };

    const onImpression = (topicCard: TopicCard, index: number) => {
        discoveryTracker.trackDiscoveryImpression(
            {
                item: {
                    id: index,
                    title: topicCard.title,
                    type: 'course_topic',
                },
            },
            {
                backendSource: 'discovery' as BackendSourceOptions,
                index: index,
                uiRegion: 'learn_essential_skills',
            },
        );
    };

    // Don't render anything during SSR or if we haven't determined the locale yet
    if (!isClient) {
        return null;
    }

    const largeCardCarouselCollection = contentfulData?.data?.largeCardCarouselCollection;
    const firstItem = largeCardCarouselCollection?.items?.[0];

    if (!firstItem) {
        // Return null if no data is available - this prevents crashes
        return null;
    }

    const {title, description, skillCardCollection} = firstItem;
    const skillCards: TopicCard[] = skillCardCollection?.items || [];

    if (!Array.isArray(skillCards) || skillCards.length === 0) {
        return null;
    }

    return (
        <EnrichedSkills title={title} subtitle={description}>
            {skillCards.map((topicCard, i) => (
                <TopicCard
                    key={`topic-card-${i}`}
                    title={topicCard.title}
                    href={topicCard.url}
                    num_enrollments={topicCard.learners}
                    backgroundImageUrl={topicCard.image?.url}
                    onClick={() => onClick(topicCard, i)}
                    trackImpression={() => onImpression(topicCard, i)}
                />
            ))}
        </EnrichedSkills>
    );
};
